import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { leadTrashService } from '../services/api';
import { Spinner } from '../components/Spinner';
import { formatIST } from '../utils/dates';
import { ArrowUturnLeftIcon, MagnifyingGlassIcon, TrashIcon } from '@heroicons/react/24/outline';

const nameOf = (u) => (u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : '');

// Whole days left before the entry is removed for good
const daysLeft = (expiresAt) => Math.max(0, Math.ceil((new Date(expiresAt) - Date.now()) / (24 * 3600 * 1000)));

export const RecycleBin = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(new Set());

  const { data, isLoading, isFetching } = useQuery(
    ['leadTrash', query, page],
    () => leadTrashService.list({ search: query || undefined, page }),
    // Always fresh on open — leads can be deleted from any screen, by anyone
    { keepPreviousData: true, staleTime: 0, refetchOnMount: 'always' }
  );
  const result = data?.data?.data || {};
  const entries = result.entries || [];
  const pagination = result.pagination || { page: 1, pages: 1, total: 0 };

  const restore = useMutation((ids) => leadTrashService.restore(ids), {
    onSuccess: (res) => {
      const { restored, failed } = res.data.data;
      if (restored) toast.success(`${restored} lead${restored === 1 ? '' : 's'} restored`);
      for (const f of failed) toast.error(`${f.customerName || 'Lead'}: ${f.reason}`, { duration: 8000 });
      setSelected(new Set());
      queryClient.invalidateQueries('leadTrash');
      queryClient.invalidateQueries('leads');
      queryClient.invalidateQueries('leadTabStats');
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Restore failed'),
  });

  const toggle = (id) => setSelected(s => { const next = new Set(s); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const allOnPage = entries.length > 0 && entries.every(e => selected.has(e._id));
  const toggleAll = () => setSelected(s => {
    const next = new Set(s);
    entries.forEach(e => (allOnPage ? next.delete(e._id) : next.add(e._id)));
    return next;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrashIcon className="h-6 w-6 text-gray-400" /> Recycle Bin
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Deleted leads stay here for {result.retentionDays || 7} days with their full history, then are removed for good.
          </p>
        </div>
        {selected.size > 0 && (
          <button
            onClick={() => restore.mutate([...selected])}
            disabled={restore.isLoading}
            className="btn-primary text-sm flex items-center gap-2"
          >
            {restore.isLoading ? <Spinner size="sm" /> : <ArrowUturnLeftIcon className="h-4 w-4" />}
            Restore {selected.size} selected
          </button>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); setPage(1); setSelected(new Set()); }}
        className="flex gap-2 max-w-md"
      >
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name, phone, brand or model"
            className="input text-sm w-full pl-9"
          />
        </div>
        <button type="submit" className="btn-secondary text-sm">Search</button>
      </form>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-center"><Spinner size="md" /></div>
        ) : entries.length === 0 ? (
          <p className="p-8 text-center text-sm text-gray-400">
            {query ? 'No deleted leads match your search.' : 'The Recycle Bin is empty.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 w-8">
                    <input type="checkbox" className="rounded" checked={allOnPage} onChange={toggleAll} />
                  </th>
                  {['Customer', 'Vehicle', 'Assigned to', 'Deleted', 'Removed in', ''].map(h => (
                    <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y divide-gray-100 ${isFetching ? 'opacity-60' : ''}`}>
                {entries.map(e => {
                  const left = daysLeft(e.expiresAt);
                  return (
                    <tr key={e._id} className={selected.has(e._id) ? 'bg-primary-50' : ''}>
                      <td className="px-4 py-2">
                        <input type="checkbox" className="rounded" checked={selected.has(e._id)} onChange={() => toggle(e._id)} />
                      </td>
                      <td className="px-4 py-2">
                        <p className="font-medium text-gray-900">{e.customerName || '—'}</p>
                        <p className="text-xs text-gray-500">{e.phone}</p>
                      </td>
                      <td className="px-4 py-2 text-gray-600">{[e.brand, e.model].filter(Boolean).join(' ') || '—'}</td>
                      <td className="px-4 py-2 text-gray-600">{nameOf(e.ownerId) || '—'}</td>
                      <td className="px-4 py-2 text-gray-600">
                        <p>{formatIST(e.deletedAt, 'dd MMM yyyy, HH:mm')}</p>
                        {e.deletedBy && <p className="text-xs text-gray-400">by {nameOf(e.deletedBy)}</p>}
                      </td>
                      <td className={`px-4 py-2 ${left <= 1 ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
                        {left <= 0 ? 'Today' : `${left} day${left === 1 ? '' : 's'}`}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => restore.mutate([e._id])}
                          disabled={restore.isLoading}
                          className="text-primary-600 hover:underline text-sm font-medium whitespace-nowrap"
                        >
                          Restore
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination.pages > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>{pagination.total} deleted lead{pagination.total === 1 ? '' : 's'}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage(p => p - 1)} disabled={page <= 1} className="btn-secondary text-sm">Previous</button>
            <span>Page {pagination.page} of {pagination.pages}</span>
            <button onClick={() => setPage(p => p + 1)} disabled={page >= pagination.pages} className="btn-secondary text-sm">Next</button>
          </div>
        </div>
      )}
    </div>
  );
};
